import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Star, Gem, Shield, Award, Lock, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToken } from '../context/TokenContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo URLs
const LOGO_FULL = 'https://customer-assets.emergentagent.com/job_745ac85c-7636-4094-9efe-1c454a28c241/artifacts/5vpvwi6a_thecollector%20logo.png';
const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_745ac85c-7636-4094-9efe-1c454a28c241/artifacts/z9d0jmi1_926ba59f-24fd-46de-bf4e-5d1b2aa8a9e9_removalai_preview.png';

// Level configurations with credit card styling
const LEVEL_CONFIG = {
  BRONZE: {
    name: 'Bronze',
    icon: Shield,
    bgGradient: 'linear-gradient(135deg, #8B5A2B 0%, #CD853F 25%, #8B4513 50%, #A0522D 75%, #8B5A2B 100%)',
    overlayPattern: 'radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
    borderColor: '#CD7F32',
    textColor: 'text-amber-200',
    accentColor: '#CD7F32',
    chipColor: 'from-amber-600 to-amber-800',
    minTokens: 0,
  },
  SILVER: {
    name: 'Silver',
    icon: Star,
    bgGradient: 'linear-gradient(135deg, #5C5C5C 0%, #A8A8A8 25%, #6B6B6B 50%, #8E8E8E 75%, #5C5C5C 100%)',
    overlayPattern: 'radial-gradient(ellipse at 70% 30%, rgba(255,255,255,0.15) 0%, transparent 50%)',
    borderColor: '#C0C0C0',
    textColor: 'text-slate-200',
    accentColor: '#C0C0C0',
    chipColor: 'from-slate-400 to-slate-600',
    minTokens: 100000,
  },
  GOLD: {
    name: 'Gold',
    icon: Award,
    bgGradient: 'linear-gradient(135deg, #B8860B 0%, #FFD700 25%, #DAA520 50%, #FFA500 75%, #B8860B 100%)',
    overlayPattern: 'radial-gradient(ellipse at 50% 50%, rgba(255,255,255,0.2) 0%, transparent 60%)',
    borderColor: '#FFD700',
    textColor: 'text-yellow-100',
    accentColor: '#FFD700',
    chipColor: 'from-yellow-500 to-yellow-700',
    minTokens: 500000,
  },
  PLATINUM: {
    name: 'Platinum',
    icon: Gem,
    bgGradient: 'linear-gradient(135deg, #1a1a2e 0%, #3d3d5c 25%, #16213e 50%, #4a4a6a 75%, #1a1a2e 100%)',
    overlayPattern: 'radial-gradient(ellipse at 20% 80%, rgba(229,228,226,0.15) 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, rgba(229,228,226,0.1) 0%, transparent 40%)',
    borderColor: '#E5E4E2',
    textColor: 'text-slate-100',
    accentColor: '#E5E4E2',
    chipColor: 'from-slate-300 to-slate-500',
    minTokens: 1000000,
  },
  VIP: {
    name: 'VIP',
    icon: Crown,
    bgGradient: 'linear-gradient(135deg, #1a0533 0%, #4c1d95 25%, #2d1b4e 50%, #7c3aed 75%, #1a0533 100%)',
    overlayPattern: 'radial-gradient(circle at 30% 70%, rgba(139,92,246,0.3) 0%, transparent 50%), radial-gradient(circle at 70% 30%, rgba(236,72,153,0.2) 0%, transparent 40%)',
    borderColor: '#8B5CF6',
    textColor: 'text-purple-100',
    accentColor: '#8B5CF6',
    chipColor: 'from-purple-500 to-pink-500',
    minTokens: 10000000,
    isVIP: true,
  },
};

const LEVEL_ORDER = ['BRONZE', 'SILVER', 'GOLD', 'PLATINUM', 'VIP'];

// Single Credit Card Component
const CreditCard = ({ levelKey, isActive, isLocked, levelData, user, tokenSymbol, isFlipped, onFlip, t }) => {
  const config = LEVEL_CONFIG[levelKey];
  const LevelIcon = config.icon;
  
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  // Generate card number style display
  const getCardNumber = () => {
    const wallet = user?.wallet_address;
    if (wallet) {
      return `${wallet.slice(0,4)} •••• •••• ${wallet.slice(-4)}`;
    }
    return '•••• •••• •••• ••••';
  };

  return (
    <div 
      className="relative w-full cursor-pointer group"
      style={{ perspective: '1500px', aspectRatio: '1.586' }}
      onMouseEnter={() => !isLocked && onFlip(true)}
      onMouseLeave={() => !isLocked && onFlip(false)}
    >
      <motion.div
        className="relative w-full h-full"
        initial={false}
        animate={{ rotateY: isFlipped && !isLocked ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Front of Card */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl"
          style={{ 
            backfaceVisibility: 'hidden',
            background: config.bgGradient,
            border: `2px solid ${config.borderColor}40`,
          }}
        >
          {/* Holographic overlay */}
          <div 
            className="absolute inset-0 opacity-60"
            style={{ background: config.overlayPattern }}
          />
          
          {/* Noise texture */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }} />

          {/* Locked Overlay */}
          {isLocked && (
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm z-20 flex flex-col items-center justify-center">
              <Lock className="w-10 h-10 text-zinc-400 mb-2" />
              <p className="text-zinc-400 font-semibold text-sm">{t('level.notEligible', { defaultValue: 'Not Eligible' })}</p>
              <p className="text-zinc-500 text-xs mt-1">
                {t('level.need', { defaultValue: 'Need' })} {formatNumber(config.minTokens)} {tokenSymbol}
              </p>
            </div>
          )}

          {/* Card Content */}
          <div className="relative z-10 p-5 h-full flex flex-col justify-between">
            {/* Header - Logo and Level */}
            <div className="flex items-start justify-between">
              <img 
                src={LOGO_FULL} 
                alt="TC Collector" 
                className="h-8 object-contain"
                style={{ filter: isLocked ? 'grayscale(1) opacity(0.5)' : 'none' }}
              />
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/30 backdrop-blur-sm ${config.textColor}`}>
                <LevelIcon className="w-4 h-4" />
                <span className="text-sm font-bold">{config.name}</span>
              </div>
            </div>

            {/* Chip */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-9 rounded-md shadow-lg" 
                style={{
                  background: `linear-gradient(135deg, #d4af37 0%, #f5d67b 50%, #d4af37 100%)`,
                }}
              >
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-8 h-6 border-l-2 border-r-2 border-zinc-600/30" />
                </div>
              </div>
              <img 
                src={LOGO_ICON} 
                alt="TC" 
                className="h-8 w-8 object-contain opacity-80"
              />
            </div>

            {/* Card Number */}
            <div>
              <p className={`font-mono text-lg tracking-widest ${config.textColor}`}>
                {getCardNumber()}
              </p>
            </div>

            {/* Footer - Name and Valid */}
            <div className="flex items-end justify-between">
              <div>
                <p className="text-[10px] text-white/50 uppercase tracking-wider">{t('level.cardHolder', { defaultValue: 'Card Holder' })}</p>
                <p className={`font-semibold ${config.textColor} truncate max-w-[150px]`}>
                  {user?.name || 'MEMBER'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-white/50 uppercase tracking-wider">{t('level.memberSince', { defaultValue: 'Member Since' })}</p>
                <p className={`font-semibold ${config.textColor}`}>
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: '2-digit', year: '2-digit' }) : '--/--'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Back of Card */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl"
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            background: config.bgGradient,
            border: `2px solid ${config.borderColor}40`,
          }}
        >
          <div 
            className="absolute inset-0 opacity-60"
            style={{ background: config.overlayPattern }}
          />

          {/* Magnetic Strip */}
          <div className="w-full h-12 bg-zinc-900 mt-6" />

          {/* Card Content */}
          <div className="relative z-10 p-5 h-[calc(100%-4.5rem)] flex flex-col justify-between">
            {/* Signature Strip with Data */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">Total Staked</p>
                  <p className={`text-2xl font-bold ${config.textColor}`}>
                    {formatNumber(levelData?.total_staked || 0)}
                  </p>
                  <p className="text-xs text-white/60">{tokenSymbol}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">APY Rate</p>
                  <p className="text-2xl font-bold text-green-400">
                    {levelData?.average_apy?.toFixed(1) || 0}%
                  </p>
                  <p className="text-xs text-white/60">per year</p>
                </div>
              </div>
            </div>

            {/* Logo Footer */}
            <div className="flex items-center justify-between">
              <img 
                src={LOGO_ICON} 
                alt="TC" 
                className="h-10 w-10 object-contain opacity-60"
              />
              <div className={`text-right ${config.textColor}`}>
                <p className="text-xs opacity-70">Powered by</p>
                <p className="font-bold">TC COLLECTOR</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Main Level Card Section with Slider
export const LevelCardSection = ({ className = '' }) => {
  const { token, user } = useAuth();
  const { getTokenSymbol } = useToken();
  const { t } = useTranslation();
  const [levelData, setLevelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [flippedCard, setFlippedCard] = useState(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    fetchLevelData();
  }, [token]);

  const fetchLevelData = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/staking/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLevelData(res.data);
      
      // Set active index to current level
      const currentLevel = res.data?.level?.level || 'BRONZE';
      const index = LEVEL_ORDER.indexOf(currentLevel);
      setActiveIndex(index >= 0 ? index : 0);
    } catch (err) {
      console.error('Failed to fetch level data:', err);
    } finally {
      setLoading(false);
    }
  };

  const currentLevel = levelData?.level?.level || 'BRONZE';
  const currentLevelIndex = LEVEL_ORDER.indexOf(currentLevel);
  const [dragDirection, setDragDirection] = useState(0);

  const handlePrev = () => {
    setDragDirection(1); // Moving right (showing previous)
    setActiveIndex(prev => Math.max(0, prev - 1));
    setFlippedCard(null);
  };

  const handleNext = () => {
    setDragDirection(-1); // Moving left (showing next)
    setActiveIndex(prev => Math.min(LEVEL_ORDER.length - 1, prev + 1));
    setFlippedCard(null);
  };

  // Handle swipe/drag
  const handleDragEnd = (event, info) => {
    const swipeThreshold = 50;
    const velocity = info.velocity.x;
    const offset = info.offset.x;

    if (offset < -swipeThreshold || velocity < -500) {
      // Swiped left - go to next
      if (activeIndex < LEVEL_ORDER.length - 1) {
        setDragDirection(-1);
        setActiveIndex(prev => prev + 1);
        setFlippedCard(null);
      }
    } else if (offset > swipeThreshold || velocity > 500) {
      // Swiped right - go to previous
      if (activeIndex > 0) {
        setDragDirection(1);
        setActiveIndex(prev => prev - 1);
        setFlippedCard(null);
      }
    }
  };

  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-zinc-800 rounded w-32 mb-4" />
          <div className="aspect-[1.586] bg-zinc-800 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {/* Slider Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-bold">{t('level.membershipCard', { defaultValue: 'Membership Card' })}</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrev}
            disabled={activeIndex === 0}
            className="p-1.5 rounded-lg bg-zinc-800/50 hover:bg-zinc-700/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
          <div className="flex gap-1.5">
            {LEVEL_ORDER.map((_, idx) => (
              <button
                key={idx}
                onClick={() => { setActiveIndex(idx); setFlippedCard(null); }}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === activeIndex 
                    ? 'bg-amber-400 w-4' 
                    : idx <= currentLevelIndex 
                      ? 'bg-zinc-500' 
                      : 'bg-zinc-700'
                }`}
              />
            ))}
          </div>
          <button
            onClick={handleNext}
            disabled={activeIndex === LEVEL_ORDER.length - 1}
            className="p-1.5 rounded-lg bg-zinc-800/50 hover:bg-zinc-700/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      {/* Card Display - Swipeable */}
      <div className="relative overflow-hidden max-w-md mx-auto touch-pan-y">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, x: dragDirection >= 0 ? 100 : -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dragDirection >= 0 ? -100 : 100 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            onDragStart={() => setFlippedCard(null)}
            className="cursor-grab active:cursor-grabbing"
          >
            <CreditCard
              levelKey={LEVEL_ORDER[activeIndex]}
              isActive={LEVEL_ORDER[activeIndex] === currentLevel}
              isLocked={activeIndex > currentLevelIndex}
              levelData={levelData}
              user={user}
              tokenSymbol={getTokenSymbol()}
              isFlipped={flippedCard === activeIndex}
              onFlip={(flip) => setFlippedCard(flip ? activeIndex : null)}
              t={t}
            />
          </motion.div>
        </AnimatePresence>
        
        {/* Swipe hint */}
        <div className="flex justify-center mt-2 gap-1 text-zinc-600 text-xs">
          <span>← {t('level.swipe', { defaultValue: 'Swipe' })} →</span>
        </div>
      </div>

      {/* Level Info */}
      <div className="mt-4 text-center">
        <p className="text-zinc-400 text-sm">
          {activeIndex === currentLevelIndex ? (
            <span className="text-amber-400">{t('level.yourCurrentLevel', { defaultValue: 'Your current level' })}</span>
          ) : activeIndex < currentLevelIndex ? (
            <span className="text-green-400">{t('level.levelAchieved', { defaultValue: 'Level achieved!' })}</span>
          ) : (
            <span>
              {t('level.stakeToUnlock', { 
                amount: LEVEL_CONFIG[LEVEL_ORDER[activeIndex]].minTokens.toLocaleString(), 
                symbol: getTokenSymbol(),
                defaultValue: `Stake ${LEVEL_CONFIG[LEVEL_ORDER[activeIndex]].minTokens.toLocaleString()} ${getTokenSymbol()} to unlock`
              })}
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

// Compact version for other uses
export const LevelBadge = ({ levelInfo, className = '' }) => {
  if (!levelInfo) return null;
  
  const config = LEVEL_CONFIG[levelInfo.level] || LEVEL_CONFIG.BRONZE;
  const LevelIcon = config.icon;

  return (
    <div 
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg backdrop-blur-sm ${className}`}
      style={{ 
        background: `linear-gradient(135deg, ${config.borderColor}20, ${config.borderColor}10)`,
        border: `1px solid ${config.borderColor}40`
      }}
    >
      <LevelIcon className={`w-4 h-4`} style={{ color: config.accentColor }} />
      <span className="font-semibold" style={{ color: config.accentColor }}>{config.name}</span>
    </div>
  );
};

// Admin Level Card (keep existing for admin panel)
export const AdminLevelCard = ({ levelInfo, tokenSymbol = 'TOKEN', className = '' }) => {
  if (!levelInfo) return null;

  const config = LEVEL_CONFIG[levelInfo.level] || LEVEL_CONFIG.BRONZE;
  const LevelIcon = config.icon;

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  return (
    <div 
      className={`rounded-xl overflow-hidden ${className}`}
      style={{ border: `1px solid ${config.borderColor}40` }}
    >
      <div 
        className="p-4"
        style={{ background: config.bgGradient }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div 
            className="p-2 rounded-lg"
            style={{ background: `${config.borderColor}20` }}
          >
            <LevelIcon className="w-5 h-5" style={{ color: config.accentColor }} />
          </div>
          <div>
            <p className="text-zinc-400 text-xs">Account Level</p>
            <h4 className="font-bold" style={{ color: config.accentColor }}>{config.name}</h4>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 rounded-lg bg-black/20">
            <p className="text-zinc-400 text-xs">Total Staked</p>
            <p className="font-bold" style={{ color: config.accentColor }}>
              {formatNumber(levelInfo.total_staked)}
            </p>
          </div>
          <div className="p-2 rounded-lg bg-black/20">
            <p className="text-zinc-400 text-xs">Avg. APY</p>
            <p className="font-bold text-green-400">{levelInfo.average_apy?.toFixed(1) || 0}%</p>
          </div>
        </div>

        {levelInfo.next_level && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-zinc-400 mb-1">
              <span>Progress to {levelInfo.next_level}</span>
              <span>{levelInfo.progress_to_next?.toFixed(0) || 0}%</span>
            </div>
            <div className="h-1.5 bg-black/30 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${levelInfo.progress_to_next || 0}%`,
                  background: config.bgGradient
                }}
              />
            </div>
            <p className="text-zinc-500 text-xs mt-1">
              {formatNumber(levelInfo.tokens_to_next)} {tokenSymbol} more needed
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Keep old LevelCard export for backwards compatibility
export const LevelCard = LevelCardSection;

export default LevelCardSection;
